/**
 * The two values that turn a live session into something a person can open,
 * shared because a support engineer pasting a replay link into a Halo ITSM
 * ticket must not be able to tell which platform recorded it.
 */

/**
 * Deploy label stored on envelopes and the session index (#30211).
 *
 * `environment.environment_name` is `production` on prod builds while the list
 * filters and player chips use the short `prod` token, so normalizing here is
 * what makes `?environment=prod` match what a recorder actually wrote.
 */
export const resolveRynReplayEnvironmentLabel = (
  environmentName: string | undefined,
  production: boolean | undefined
): string => {
  const isProd = production === true;
  const raw = environmentName?.trim() || (isProd ? 'prod' : 'dev');
  return raw === 'production' ? 'prod' : raw;
};

/**
 * Compose the absolute player URL for a session.
 *
 * ABSOLUTE is the requirement, not a preference. The load-bearing consumer is a
 * link stamped onto a Halo ITSM or help-desk ticket, which is then opened from
 * the ticketing system or an email rather than from inside the app that wrote
 * it, so a relative path resolves against the wrong host. On mobile there is no
 * origin to fall back to at all, which is why `playerBaseUrl` is the configured
 * value first and an origin only second.
 *
 * Trailing slashes are tolerated because this value is hand-typed into Azure
 * App Configuration.
 */
export const rynReplaySessionUrl = (
  sessionId: string | undefined,
  playerBaseUrl: string | undefined,
  fallbackOrigin?: string
): string | undefined => {
  if (!sessionId) {
    return undefined;
  }
  const base = (playerBaseUrl?.trim() || fallbackOrigin || '').replace(
    /\/+$/,
    ''
  );
  return `${base}/replay/${sessionId}`;
};
